import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, X, ListChecks, ArrowRight } from "lucide-react";
import { fetchOnboardingChecklist, dismissOnboarding } from "@/lib/api";
import ActivationCompleteModal from "@/components/ActivationCompleteModal";

export const CHECKLIST = {
  card: "first-run-checklist",
  item: "first-run-item",
  dismiss: "first-run-dismiss",
  progress: "first-run-progress",
};

const ITEM_LINKS = {
  add_child_1:     "/app/children",
  add_child_2:     "/app/children",
  enable_briefing: "/app/settings",
  review_health:   "/app",
  first_briefing:  "/app/briefing",
};

const CELEBRATION_KEY = "kidhq.activation.celebrated";

/** Dismissible First-Run Checklist. Hides at 100% complete after celebrating
 * the user with an Activation-Complete modal (shown once only). */
export default function FirstRunChecklist() {
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(() => !!localStorage.getItem("kidhq.firstrun.dismissed"));
  const [showCelebration, setShowCelebration] = useState(false);

  useEffect(() => {
    if (dismissed) return;
    fetchOnboardingChecklist().then(d => {
      setData(d);
      // Celebrate only when (a) the user just finished AND we haven't celebrated yet
      if (d?.just_finished && !localStorage.getItem(CELEBRATION_KEY)) {
        setShowCelebration(true);
        localStorage.setItem(CELEBRATION_KEY, "1");
      }
    }).catch(() => {});
  }, [dismissed]);

  const handleDismiss = async () => {
    setDismissed(true);
    localStorage.setItem("kidhq.firstrun.dismissed", "1");
    try { await dismissOnboarding(); } catch { /* non-fatal */ }
  };

  if (dismissed && !showCelebration) return null;
  if (!data) return null;

  // Auto-hide the checklist card itself at 100% — but the modal still shows
  if (data.completed === data.total) {
    return <ActivationCompleteModal open={showCelebration} onClose={() => setShowCelebration(false)} />;
  }

  const pct = Math.round((data.completed / data.total) * 100);

  return (
    <div data-testid={CHECKLIST.card} className="fcc-card overflow-hidden">
      <div className="p-7 sm:p-8">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-2xl bg-[var(--fcc-green-soft)] flex items-center justify-center">
              <ListChecks className="w-5 h-5 text-[var(--fcc-green)]" />
            </span>
            <div>
              <p className="label-overline">Get started with KidHQ</p>
              <h3 className="font-display text-xl text-[var(--fcc-ink)] mt-0.5">First-run checklist</h3>
            </div>
          </div>
          <button
            data-testid={CHECKLIST.dismiss}
            onClick={handleDismiss}
            aria-label="Dismiss"
            className="text-[var(--fcc-muted)] hover:text-[var(--fcc-ink)] w-8 h-8 rounded-full hover:bg-[var(--fcc-cream-2)] flex items-center justify-center"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div data-testid={CHECKLIST.progress} className="mt-5">
          <div className="flex items-center justify-between text-xs text-[var(--fcc-muted)]">
            <span>{data.completed} of {data.total} complete</span>
            <span>{pct}%</span>
          </div>
          <div className="mt-2 h-2 bg-[var(--fcc-cream-2)] rounded-full overflow-hidden">
            <div className="h-2 bg-[var(--fcc-green)]" style={{ width: `${pct}%`, transition: "width 0.4s" }} />
          </div>
        </div>

        <ul className="mt-5 space-y-2">
          {data.items.map(it => {
            const Icon = it.done ? CheckCircle2 : Circle;
            const color = it.done ? "var(--fcc-green)" : "var(--fcc-muted)";
            return (
              <li key={it.id} data-testid={CHECKLIST.item} className="flex items-center gap-3 py-1">
                <Icon className="w-5 h-5 shrink-0" style={{ color }} />
                <span className={`text-sm flex-1 ${it.done ? "text-[var(--fcc-muted)] line-through" : "text-[var(--fcc-ink)]"}`}>
                  {it.label}
                </span>
                {!it.done && ITEM_LINKS[it.id] && (
                  <Link to={ITEM_LINKS[it.id]} className="text-xs font-semibold text-[var(--fcc-green)] hover:underline flex items-center gap-1">
                    Go <ArrowRight className="w-3 h-3" />
                  </Link>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
